/**
 * MisItemsAgGridHook
 *
 * AG Grid hook for the MIS Details modal — merchant-rollup grid.
 * Mounts when the modal opens, destroyed when it closes (LiveView if/end).
 *
 * `data-rows` carries ONE row per merchant (row_type: "parent"), aggregated
 * server-side by SettlementCore.Context.list_merchant_totals_for_mis/1 — not
 * the full merchant × scheme × card_type item set. A merchant's children
 * (its original item rows, plus any adjustment/held synthetic rows) are
 * fetched lazily on expand via the "expand_merchant" LiveView event and
 * cached client-side, so re-expanding never re-fetches.
 *
 * Pagination is by merchant (10/page), built by hand below the grid —
 * AG Grid's own row-count pagination would split one merchant's expanded
 * children across pages.
 *
 * Deliberately avoids AG Grid Enterprise's native `rowGroup`: the vendored
 * ag-grid-community/ag-grid-enterprise bundles in this app are not a clean
 * npm-versioned install, and native row-grouping against a similar setup
 * elsewhere crashed with `getDisplayedChildren is not a function`. Expand/
 * collapse here is entirely manual: recompute the visible row list and hand
 * it to the grid via setGridOption("rowData", ...).
 */

const PARENTS_PER_PAGE = 10;

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));

const LOADING_ROW = { row_type: "loading" };

const MisItemsAgGridHook = {
  mounted() {
    const root = this.el;
    this.parentRows = JSON.parse(root.dataset.rows || "[]");
    this.childCache = new Map(); // merchant_mid -> rows[]
    this.expanded = new Set();
    this.currentPage = 0;

    // `root` (this.el) is the phx-update="ignore" boundary — LiveView never
    // touches its subtree again after mount, so both the grid mount point
    // and the hand-built pagination footer live as its children, safe from
    // re-render patching regardless of other state changes on the page.
    const container = document.createElement("div");
    container.style.width = "100%";
    container.className = "ag-theme-alpine";
    root.appendChild(container);

    // ── Cell renderers ──────────────────────────────────────────────────────

    const amountCell = (params) => {
      const val = params.value;
      if (val === null || val === undefined || val === "") return "0.00";
      const n = parseFloat(val);
      return isNaN(n)
        ? val
        : n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const midMerchantCell = (params) => {
      const d = params.data || {};
      if (d.row_type === "loading") return "";
      if (d.row_type === "parent") {
        const icon = this.expanded.has(d.merchant_mid) ? "▾" : "▸";
        return (
          `<span class="inline-block w-4 text-center text-gray-500 dark:text-gray-400 select-none">${icon}</span>` +
          `<span class="font-semibold text-gray-900">${escapeHtml(d.merchant_mid)}</span>` +
          (d.merchant_name ? `<span class="text-gray-500"> — ${escapeHtml(d.merchant_name)}</span>` : "")
        );
      }
      return `<span class="pl-4 text-gray-500 font-mono text-xs">${escapeHtml(d.merchant_mid)}</span>`;
    };

    const schemeCell = (params) => {
      const d = params.data || {};
      if (d.row_type === "loading") return `<span class="text-gray-400 italic">Loading…</span>`;
      if (d.row_type === "adjustment") {
        return `<span class="italic font-medium text-amber-700">${escapeHtml(d.label)}</span>`;
      }
      if (d.row_type === "held") {
        return `<span class="italic font-medium text-orange-700">${escapeHtml(d.label)}</span>`;
      }
      return escapeHtml(d.scheme_name || "");
    };

    // ── Column definitions ──────────────────────────────────────────────────

    const columnDefs = [
      {
        colId: "mid_merchant",
        headerName: "MID / Merchant",
        pinned: "left",
        width: 220,
        cellRenderer: midMerchantCell,
        filter: "agTextColumnFilter",
        valueGetter: (params) => params.data && params.data.merchant_mid,
      },
      {
        field: "scheme_name",
        headerName: "Scheme",
        flex: 1,
        minWidth: 130,
        cellRenderer: schemeCell,
        filter: "agTextColumnFilter",
      },
      {
        field: "card_type_code",
        headerName: "Card Type",
        flex: 1,
        minWidth: 110,
        filter: "agTextColumnFilter",
      },
      {
        field: "transaction_count",
        headerName: "Txn Count",
        type: "numericColumn",
        flex: 0.7,
        minWidth: 90,
        filter: "agNumberColumnFilter",
      },
      {
        field: "gross_amount",
        headerName: "Gross (AED)",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 1,
        minWidth: 130,
        filter: "agNumberColumnFilter",
      },
      {
        field: "mdr_amount",
        headerName: "MDR",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 0.9,
        minWidth: 110,
        filter: "agNumberColumnFilter",
      },
      {
        field: "vat_amount",
        headerName: "VAT",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 0.8,
        minWidth: 100,
        filter: "agNumberColumnFilter",
      },
      {
        field: "interchange_fee",
        headerName: "Interchange",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 1,
        minWidth: 120,
        filter: "agNumberColumnFilter",
      },
      {
        field: "margin_contribution",
        headerName: "Margin",
        type: "numericColumn",
        cellRenderer: amountCell,
        flex: 0.9,
        minWidth: 110,
        filter: "agNumberColumnFilter",
      },
      {
        field: "net_payable",
        headerName: "Net Payable",
        type: "numericColumn",
        cellRenderer: amountCell,
        width: 155,
        minWidth: 140,
        pinned: "right",
        cellStyle: { fontWeight: "bold" },
        filter: "agNumberColumnFilter",
      },
    ];

    // ── Visible-row computation & pagination ────────────────────────────────

    const totalPages = () => Math.max(1, Math.ceil(this.parentRows.length / PARENTS_PER_PAGE));

    const currentPageParents = () => {
      const start = this.currentPage * PARENTS_PER_PAGE;
      return this.parentRows.slice(start, start + PARENTS_PER_PAGE);
    };

    const computeVisibleRows = () => {
      const visible = [];
      for (const parent of currentPageParents()) {
        visible.push(parent);
        if (this.expanded.has(parent.merchant_mid)) {
          visible.push(...(this.childCache.get(parent.merchant_mid) || [LOADING_ROW]));
        }
      }
      return visible;
    };

    const renderGrid = () => {
      if (this.grid) this.grid.setGridOption("rowData", computeVisibleRows());
      renderPagination();
    };
    this.renderGrid = renderGrid;

    // ── Pagination footer (hand-built — paginates by merchant, not by row) ──

    const footer = document.createElement("div");
    footer.className =
      "flex items-center justify-between px-1 py-2 text-xs text-gray-500 border-t border-gray-200";
    root.appendChild(footer);
    this.footer = footer;

    const renderPagination = () => {
      const total = this.parentRows.length;
      const start = total === 0 ? 0 : this.currentPage * PARENTS_PER_PAGE + 1;
      const end = Math.min(total, (this.currentPage + 1) * PARENTS_PER_PAGE);

      footer.innerHTML = `
        <button type="button" data-page-action="prev"
          class="px-2 py-1 rounded border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          ${this.currentPage === 0 ? "disabled" : ""}>‹ Prev</button>
        <span>Page ${this.currentPage + 1} of ${totalPages()} (merchants ${start}-${end} of ${total})</span>
        <button type="button" data-page-action="next"
          class="px-2 py-1 rounded border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
          ${this.currentPage >= totalPages() - 1 ? "disabled" : ""}>Next ›</button>
      `;
    };

    footer.addEventListener("click", (e) => {
      const action = e.target.closest("[data-page-action]")?.dataset.pageAction;
      if (!action) return;
      if (action === "prev" && this.currentPage > 0) this.currentPage -= 1;
      if (action === "next" && this.currentPage < totalPages() - 1) this.currentPage += 1;
      renderGrid();
    });

    // ── Expand / collapse ────────────────────────────────────────────────────

    const toggleExpand = (merchantMid) => {
      if (this.expanded.has(merchantMid)) {
        this.expanded.delete(merchantMid);
        renderGrid();
        return;
      }

      this.expanded.add(merchantMid);
      if (this.childCache.has(merchantMid)) {
        renderGrid();
        return;
      }

      renderGrid(); // shows the loading placeholder row immediately
      this.pushEvent("expand_merchant", { merchant_mid: merchantMid }, (reply) => {
        this.childCache.set(merchantMid, (reply && reply.rows) || []);
        renderGrid();
      });
    };

    // Applied per-cell (not via getRowClass): AG Grid's own zebra-striping
    // sets row background through its own CSS variables/selectors, which can
    // outrank a row-level class at equal specificity. cellClass adds the
    // class straight onto each .ag-cell (including in the pinned-left/right
    // containers), which is the same per-element approach the a0f874b dark-
    // mode fix used for the Risk grids' custom cell HTML — proven to work
    // against this app's theme.css .theme-dark overrides.
    const rowTypeCellClass = (params) => {
      const t = params.data && params.data.row_type;
      if (t === "adjustment") return "bg-amber-50 italic";
      if (t === "held") return "bg-orange-50 italic";
      if (t === "parent") return "font-semibold";
      return "";
    };

    // ── Grid initialization ─────────────────────────────────────────────────

    this.grid = window.agGrid.createGrid(container, {
      columnDefs,
      rowData: computeVisibleRows(),
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        cellClass: rowTypeCellClass,
      },
      onCellClicked: (params) => {
        const d = params.data;
        if (d && d.row_type === "parent" && params.column.getColId() === "mid_merchant") {
          toggleExpand(d.merchant_mid);
        }
      },
      pagination: false,
      domLayout: "autoHeight",
      popupParent: document.body,
      suppressMovableColumns: false,
    });

    renderPagination();

    // Resize after mount so columns fill the modal width
    setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
  },

  updated() {
    if (this.grid) setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
  },

  destroyed() {
    if (this.grid) {
      this.grid.destroy();
      this.grid = null;
    }
    if (this.footer) {
      this.footer.remove();
      this.footer = null;
    }
  },
};

export default MisItemsAgGridHook;
